import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

// Legal document registry + click-through acceptance (claro-elixir PR #459).
// The backend resolves which document this user must accept by role
// (clinic owners → full MSA, practitioners → Parts A+C, others → Part A) and
// only published documents are ever required — while the legal kit is in
// draft, `pending` is always false and the gate stays invisible.

export interface RequiredAgreement {
  uuid: string
  doc_type: string
  jurisdiction: string
  version: string
  title: string
  checksum: string
  requires_acceptance: boolean
  audience: string | null
  material: boolean
  effective_at: string | null
  accepted_at: string | null
}

export interface AgreementStatus {
  required: RequiredAgreement[]
  pending: boolean
}

export interface AcceptAgreementResponse {
  uuid: string
  doc_type: string
  version: string
  checksum: string
  accepted_at: string
  surface: string | null
}

/** The full current document for one doc_type, markdown `content` included —
 *  used to render the terms inline in the acceptance gate. */
export interface LegalDocument {
  uuid: string
  doc_type: string
  jurisdiction: string
  version: string
  title: string
  content: string
  checksum: string
  requires_acceptance: boolean
  audience: string | null
  material: boolean
  effective_at: string | null
}

/** A document version this user accepted (Settings › Legal, CLA-1845). */
export interface AcceptedAgreement {
  document_uuid: string
  doc_type: string
  version: string
  title: string
  jurisdiction: string
  accepted_at: string
  /** False once a newer version of this document is the published one. */
  current: boolean
}

/** An owner agreement (e.g. the MSA) accepted for one of the user's clinics. */
export interface ClinicAgreement {
  document_uuid: string
  doc_type: string
  version: string
  title: string
  jurisdiction: string
  clinic_name: string
  accepted_at: string
}

/** A current document nobody click-accepts (Privacy Policy, AUP and so on). */
export interface RelatedLegalDocument {
  document_uuid: string
  doc_type: string
  version: string
  title: string
  jurisdiction: string
}

export interface AcceptedAgreements {
  accepted: AcceptedAgreement[]
  clinic: ClinicAgreement[]
  related: RelatedLegalDocument[]
}

/** Public page slug for a registry doc_type — the standalone server-rendered
 *  page, used for "open in a new tab" links (not embedded). */
export function legalDocumentPath(docType: string): string {
  const slugs: Record<string, string> = {
    msa: 'msa',
    user_terms: 'user-terms',
    practitioner_terms: 'practitioner-terms',
    privacy_policy: 'privacy-policy',
    acceptable_use_policy: 'acceptable-use-policy',
    subprocessor_list: 'subprocessor-list',
    dpa: 'data-processing-agreement',
  }
  return `/legal/${slugs[docType] ?? docType}`
}

export function useAgreementStatus(enabled = true) {
  return useQuery({
    queryKey: ['legal-agreements'],
    queryFn: () => api.get<AgreementStatus>('/legal/agreements'),
    enabled,
    staleTime: 5 * 60 * 1000,
    // Never block the app on a flaky check — the gate fails open.
    retry: 1,
  })
}

/** Fetch the full current document (with markdown body) for inline display.
 *  `/api/v1/legal/*` is exempt from the acceptance gate, so this is reachable
 *  while the gate is blocking. */
export function useLegalDocument(docType: string | undefined) {
  return useQuery({
    queryKey: ['legal-document', docType],
    queryFn: () => api.get<LegalDocument>(`/legal/documents/${docType}`),
    enabled: !!docType,
    staleTime: 60 * 60 * 1000,
  })
}

export function useAcceptAgreement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (documentUuid: string) =>
      api.post<AcceptAgreementResponse>('/legal/agreements', {
        document_uuid: documentUuid,
        surface: 'web',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-agreements'] })
      queryClient.invalidateQueries({ queryKey: ['legal-accepted'] })
    },
  })
}

/** Everything this user can re-read from Settings › Legal: their own
 *  acceptances, their clinic's owner agreements, and the related documents. */
export function useAcceptedAgreements() {
  return useQuery({
    queryKey: ['legal-accepted'],
    queryFn: () => api.get<AcceptedAgreements>('/legal/accepted'),
    staleTime: 5 * 60 * 1000,
  })
}

/** One listed document version, body included. The server serves only uuids
 *  in `useAcceptedAgreements`' lists, so an older accepted version stays
 *  readable without being public. */
export function useAcceptedDocument(documentUuid: string | null) {
  return useQuery({
    queryKey: ['legal-accepted-document', documentUuid],
    queryFn: () => api.get<LegalDocument>(`/legal/accepted/${documentUuid}`),
    enabled: !!documentUuid,
    // A version's text never changes (a change is a new version).
    staleTime: Infinity,
  })
}
